/**
 * API Configuration
 * Centralized configuration for all backend services
 */

// Get environment variables with fallbacks
const getEnvVar = (key, fallback) => {
  return import.meta.env[key] || fallback
}

// Service URLs
export const API_CONFIG = {
  // Identity Service (Authentication, User Management)
  IDENTITY: {
    baseURL: getEnvVar('VITE_IDENTITY_API_URL', '/api/identity'),
    timeout: 10000,
  },
  
  // Timekeeping Service
  TIMEKEEPING: {
    baseURL: getEnvVar('VITE_TIMEKEEPING_API_URL', '/api/timekeeping'),
    timeout: 10000,
  },
  
  // HR Service
  HR: {
    baseURL: getEnvVar('VITE_HR_API_URL', '/api/hr'),
    timeout: 10000,
  },
  
  // Projects Service
  PROJECTS: {
    baseURL: getEnvVar('VITE_PROJECTS_API_URL', '/api/projects'),
    timeout: 10000,
  },
  
  // CRM Service
  CRM: {
    baseURL: getEnvVar('VITE_CRM_API_URL', '/api/crm'),
    timeout: 10000,
  },

  // Document Control Service
  DOCUMENTS: {
    baseURL: getEnvVar('VITE_DOCUMENTS_API_URL', '/api/documents'),
    timeout: 10000,
  },

  // Bid Workup / Estimating Service (LLM calls can take 30s+)
  BID_WORKUP: {
    baseURL: getEnvVar('VITE_BID_WORKUP_API_URL', '/api/bidworkup'),
    timeout: 60000,
  },

  // Fleet Management Service
  FLEET: {
    baseURL: getEnvVar('VITE_FLEET_API_URL', '/api/fleet'),
    timeout: 10000,
  },

  // Office Board Service
  OFFICE_BOARD: {
    baseURL: getEnvVar('VITE_OFFICE_BOARD_API_URL', '/api/office-board'),
    timeout: 10000,
  },

  // PDF Reader Service (Piping ISO extraction & takeoff)
  PDF_READER: {
    baseURL: getEnvVar('VITE_PDF_READER_API_URL', '/api/pdf-reader'),
    timeout: 120000,
  },

  // Inventory Management Service
  INVENTORY: {
    baseURL: getEnvVar('VITE_INVENTORY_API_URL', '/api/inventory'),
    timeout: 10000,
  },

  // Forms Platform Service (nginx /api/forms/* -> :8013/api/v1/*)
  FORMS: {
    baseURL: getEnvVar('VITE_FORMS_API_URL', '/api/forms'),
    timeout: 10000,
  },

  // Capital Projects Service (nginx /api/capital/* -> the capital module's /api/v1/*)
  CAPITAL: {
    baseURL: getEnvVar('VITE_CAPITAL_API_URL', '/api/capital'),
    timeout: 10000,
  },

  // Capex Service (nginx /api/capex/* -> the capex app's /api/v1/*, port 8015).
  //
  // A SEPARATE APP FROM `capital`, not a rename of it. Capital owns the post-award record —
  // projects, cost codes, frozen baselines, awards. Capex owns the running job's financials: WIP,
  // job cost against the PM's forecast, change orders, billing, cash flow, risk, closeout and its
  // own bid workbench. Every one of its tables is prefixed `capex_` and references none of
  // capital's, so the two coexist and either can be retired without the other.
  //
  // 30s rather than the 10s its neighbours use: the WIP schedule and the company overview
  // aggregate every job in the portfolio for a period.
  CAPEX: {
    baseURL: getEnvVar('VITE_CAPEX_API_URL', '/api/capex'),
    timeout: 30000,
  },

  // Invoicing Service (nginx /api/invoicing/* -> the invoicing app's /api/v1/*, port 8016).
  //
  // The owner's second vendored TenBar module — pay applications (G702/G703, unit price), T&M
  // invoices from signed field tickets, receivables under the Texas prompt-payment clocks, cash
  // application, lien waivers and the billing forecast that feeds capex cash flow. Its only capex
  // coupling is by VALUE (inv_contracts.job_no carries the capex job number); either app runs
  // without the other.
  //
  // 30s like CAPEX above and for the same reason: the billing desk, receivables and forecast pages
  // aggregate every contract in the portfolio for a period.
  INVOICING: {
    baseURL: getEnvVar('VITE_INVOICING_API_URL', '/api/invoicing'),
    timeout: 30000,
  },

  // The AI Office Worker (nginx /api/ow/* -> its web API on 8017, path UNCHANGED: its API lives at
  // /api/ow/v1). It runs inside the backend container and is signed in by this app's own token, which
  // the shared client attaches. 30s: the inbox and weekly pages read across a company's tickets.
  OFFICE_WORKER: {
    baseURL: getEnvVar('VITE_OFFICE_WORKER_API_URL', '/api/ow/v1'),
    timeout: 30000,
  },
}

// Authentication tokens and storage keys
export const AUTH_CONFIG = {
  TOKEN_KEY: 'bfs_access_token',
  USER_KEY: 'bfs_user',
  REFRESH_TOKEN_KEY: 'bfs_refresh_token',
}

// API defaults
export const DEFAULT_CONFIG = {
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
}

// Error messages
export const API_ERRORS = {
  UNAUTHORIZED: 'Your session has expired. Please log in again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  // A 409 used to fall through to SERVER_ERROR, so "somebody changed this while you were working"
  // and "the server is broken" read as the same sentence. They call for opposite actions.
  CONFLICT: 'This changed while you were working on it. Reload and try again.',
  SERVER_ERROR: 'An unexpected error occurred. Please try again later.',
  NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection.',
  TIMEOUT: 'The request took too long to complete. Please try again.',
}
